import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Eye, Shield, Zap, BarChart3, Lock, CheckCircle, XCircle, CreditCard, Sun, Moon, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { T } from '@/utils/translations';
import { createStripeCheckout } from '@/utils/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme, lang, setLang } = useApp();
  const t = T[lang] || T.fr;

  const handleSubscribe = async (planId) => {
    try {
      const data = await createStripeCheckout(
        planId,
        window.location.origin + '/app?subscription=success',
        window.location.origin + '/#pricing'
      );
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    }
  };

  return (
    <div className="landing">
      {/* Header */}
      <header className="landing-header">
        <div className="logo">
          <div className="logo-mark">Ax</div>
          <span className="logo-text">AX<span>-IOM</span></span>
        </div>
        <nav className="landing-nav">
          <a href="#problem">{lang === 'fr' ? 'Problème' : 'Problem'}</a>
          <a href="#solution">{lang === 'fr' ? 'Solution' : 'Solution'}</a>
          <a href="#pricing">{lang === 'fr' ? 'Tarifs' : 'Pricing'}</a>
        </nav>
        <div className="header-actions">
          <button className="btn btn-ghost btn-sm" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button 
            className={`btn btn-ghost btn-sm ${lang === 'fr' ? '' : 'active'}`}
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
          >
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/app')}>
            {lang === 'fr' ? "Accéder" : 'Access'} <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="hero-tag">
          <Sparkles size={16} />
          {lang === 'fr' ? 'Intelligence décisionnelle' : 'Decision Intelligence'}
        </div>
        <h1 className="hero-title">{t.hero.title}</h1>
        <p className="hero-tagline">{t.hero.tagline}</p>
        <p className="hero-desc">{t.hero.desc}</p>
        <div className="hero-cta">
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/app')}>
            {t.hero.cta} <ChevronRight size={18} />
          </button>
          <button className="btn btn-secondary btn-lg">
            <Eye size={18} /> {t.hero.example}
          </button>
        </div>
      </section>

      {/* Problem */}
      <section className="section alt" id="problem">
        <div className="section-inner">
          <div className="section-label">{t.problem.label}</div>
          <h2 className="section-title">{t.problem.title}</h2>
          <p className="section-text">{t.problem.text}</p>
        </div>
      </section>

      {/* Solution */}
      <section className="section" id="solution">
        <div className="section-inner">
          <div className="section-label">{t.solution.label}</div>
          <h2 className="section-title">{t.solution.title}</h2>
          <p className="section-text">{t.solution.text}</p>
          <div className="solution-grid">
            {t.solution.cards.map((c, i) => {
              const icons = [Target, Eye, Shield];
              const Icon = icons[i];
              return (
                <div key={i} className="solution-card">
                  <div className="solution-icon"><Icon size={24} /></div>
                  <h3 className="solution-title">{c.title}</h3>
                  <p className="solution-desc">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section" id="pricing">
        <div className="section-inner">
          <div className="section-label">{t.pricing.label}</div>
          <h2 className="section-title">{t.pricing.title}</h2>
          <p className="section-text" style={{ marginBottom: 0 }}>{t.pricing.annual}</p>
          <div className="pricing-grid">
            {t.pricing.plans.map((p, i) => {
              const planIds = ['core_monthly', 'pro_monthly', 'elite_monthly'];
              return (
                <div key={i} className={`pricing-card ${p.featured ? 'featured' : ''}`}>
                  {p.featured && <div className="pricing-badge">{lang === 'fr' ? 'Populaire' : 'Popular'}</div>}
                  <div className="pricing-name">{p.name}</div>
                  <div className="pricing-desc">{p.desc}</div>
                  <div className="pricing-price">
                    <span className="pricing-amount">€{p.price}</span>
                    <span className="pricing-period">/{lang === 'fr' ? 'mois' : 'mo'}</span>
                  </div>
                  <div className="pricing-annual">€{Math.round(p.price * 12 * 0.8)}/{lang === 'fr' ? 'an' : 'yr'}</div>
                  <ul className="pricing-features">
                    {p.features.map((f, j) => <li key={j}><CheckCircle size={16} /> {f}</li>)}
                    {p.disabled.map((f, j) => <li key={`d${j}`} className="disabled"><XCircle size={16} /> {f}</li>)}
                  </ul>
                  <button
                    className={`btn ${p.featured ? 'btn-primary' : 'btn-secondary'} btn-full`}
                    onClick={() => handleSubscribe(planIds[i])}
                  >
                    <CreditCard size={16} />
                    {lang === 'fr' ? "S'abonner" : 'Subscribe'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="disclaimer-section">
        <div className="disclaimer-box">
          <div className="disclaimer-label">{t.disclaimer.label}</div>
          <p className="disclaimer-text">{t.disclaimer.text}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-copy">
          © 2025 AX-IOM. {lang === 'fr' ? "Licence d'exploitation" : 'Licensed to'} <strong>2bintec</strong> - 78, avenue des Champs Elysées, 75008 Paris
        </div>
        <div className="footer-links">
          <a href="#">{lang === 'fr' ? 'Mentions légales' : 'Terms'}</a>
          <a href="#">{lang === 'fr' ? 'Confidentialité' : 'Privacy'}</a>
          <a href="#">Contact</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
